import React, { useEffect } from 'react';
import { motion } from 'framer-motion';

const TermsConditions = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="w-full bg-brand-cream">
      <section className="pt-40 pb-20 bg-brand-olive text-white text-center">
        <div className="container mx-auto px-6">
          <p className="font-script text-3xl text-brand-cream mb-2">Legal Information</p>
          <h1 className="font-serif text-4xl md:text-6xl uppercase tracking-[0.2em]">Terms & Conditions</h1>
        </div>
      </section>

      <section className="py-24">
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white p-8 md:p-16 shadow-2xl border border-brand-olive/5"
          >
            <p className="text-brand-olive/50 text-[10px] font-black uppercase tracking-widest mb-8">
              Last Updated: May 20, 2024
            </p>

            <div className="prose prose-olive max-w-none space-y-10 text-brand-olive/80 leading-relaxed font-light">
              <div>
                <h2 className="font-serif text-2xl text-brand-olive uppercase tracking-widest mb-4">Introduction</h2>
                <p>
                  Welcome to the R. Venue Apartelle website. By using this website, submitting an inquiry, or making a booking request, you agree to these Terms and Conditions.
                </p>
                <p className="mt-4">
                  Please read these terms carefully before using the website or booking with us.
                </p>
              </div>

              <div>
                <h2 className="font-serif text-2xl text-brand-olive uppercase tracking-widest mb-4">About R. Venue Apartelle</h2>
                <p>
                  R. Venue Apartelle provides accommodation in Lucban, Quezon, Philippines. Information shown on this website is provided to help guests learn about the property, amenities, location, booking process, and guest policies.
                </p>
              </div>

              <div>
                <h2 className="font-serif text-2xl text-brand-olive uppercase tracking-widest mb-4">Website Information</h2>
                <p>
                  We make every effort to keep the information on this website accurate and updated. However, details such as rates, availability, amenities, policies, and services may change without prior notice.
                </p>
              </div>

              <div>
                <h2 className="font-serif text-2xl text-brand-olive uppercase tracking-widest mb-4">Bookings and Availability</h2>
                <p>
                  Submitting a booking request or availability inquiry through the website does not automatically guarantee a confirmed booking.
                </p>
                <p className="mt-4">
                  A booking is considered confirmed only after R. Venue Apartelle has reviewed the request and provided confirmation through our official communication channels.
                </p>
                <p className="mt-4">
                  Guests are responsible for ensuring that the booking details they provide are accurate and complete.
                </p>
              </div>

              <div>
                <h2 className="font-serif text-2xl text-brand-olive uppercase tracking-widest mb-4">Check-In and Check-Out</h2>
                <p>
                  Check-in and check-out details will be provided upon booking confirmation.
                </p>
                <p className="mt-4 font-bold">
                  Check-out is at 11:00 AM.
                </p>
                <p className="mt-4">
                  Requests for early check-in or late check-out are subject to availability and approval.
                </p>
              </div>

              <div>
                <h2 className="font-serif text-2xl text-brand-olive uppercase tracking-widest mb-4">Guest Responsibilities</h2>
                <p>Guests are expected to:</p>
                <ul className="list-disc pl-5 mt-4 space-y-2">
                  <li>Provide accurate booking and contact information</li>
                  <li>Follow property rules and staff instructions</li>
                  <li>Respect other guests, neighbors, staff, and the property</li>
                  <li>Use the room and facilities responsibly</li>
                  <li>Report any damage, issue, or concern as soon as possible</li>
                </ul>
              </div>

              <div>
                <h2 className="font-serif text-2xl text-brand-olive uppercase tracking-widest mb-4">House Rules</h2>
                <p>To help maintain a clean, safe, and comfortable environment, guests must follow the house rules of R. Venue Apartelle.</p>
                <p className="mt-4 font-bold">Pets are not allowed in the room.</p>
                <p className="mt-4">Smoking may be restricted to designated areas only, if available.</p>
                <p className="mt-4">Guests may be held responsible for damage, loss, excessive cleaning, or violations of property rules.</p>
              </div>

              <div>
                <h2 className="font-serif text-2xl text-brand-olive uppercase tracking-widest mb-4">Parking</h2>
                <p>Parking is available at Lavander Building.</p>
                <p className="mt-4">Parking availability, access, and conditions may be subject to space, property guidelines, and staff instructions.</p>
              </div>

              <div>
                <h2 className="font-serif text-2xl text-brand-olive uppercase tracking-widest mb-4">Payments, Rates, and Fees</h2>
                <p>Rates and payment terms may vary depending on dates, room type, length of stay, promotions, and availability.</p>
                <p className="mt-4">Any required deposit, balance payment, cancellation fee, or additional charge will be communicated during the booking process.</p>
                <p className="mt-4 text-brand-olive/60 italic">R. Venue Apartelle reserves the right to update rates or fees when necessary.</p>
              </div>

              <div>
                <h2 className="font-serif text-2xl text-brand-olive uppercase tracking-widest mb-4">Cancellations and Changes</h2>
                <p>Cancellation, rescheduling, and refund terms may depend on the booking arrangement, date of cancellation, and any special conditions provided at the time of booking.</p>
                <p className="mt-4">Guests should contact R. Venue Apartelle as soon as possible for any booking changes or cancellation requests.</p>
              </div>

              <div>
                <h2 className="font-serif text-2xl text-brand-olive uppercase tracking-widest mb-4">Guest Belongings</h2>
                <p>Guests are responsible for their personal belongings. R. Venue Apartelle is not liable for lost, misplaced, or damaged personal items unless required by applicable law.</p>
              </div>

              <div>
                <h2 className="font-serif text-2xl text-brand-olive uppercase tracking-widest mb-4">Limitation of Liability</h2>
                <p>R. Venue Apartelle aims to provide a safe and comfortable stay. However, guests use the website, rooms, facilities, and services at their own responsibility.</p>
                <p className="mt-4">To the extent permitted by law, R. Venue Apartelle is not liable for indirect, incidental, or unexpected losses arising from website use, booking issues, travel changes, or circumstances beyond our control.</p>
              </div>

              <div>
                <h2 className="font-serif text-2xl text-brand-olive uppercase tracking-widest mb-4">Website Use</h2>
                <p>You agree not to misuse this website, attempt unauthorized access, interfere with website functionality, or use the website for unlawful purposes.</p>
              </div>

              <div>
                <h2 className="font-serif text-2xl text-brand-olive uppercase tracking-widest mb-4">Third-Party Links or Services</h2>
                <p>This website may include links, embedded content, review widgets, booking tools, maps, or other third-party services. R. Venue Apartelle is not responsible for the content, policies, or practices of third-party websites or services.</p>
              </div>

              <div>
                <h2 className="font-serif text-2xl text-brand-olive uppercase tracking-widest mb-4">Updates to These Terms</h2>
                <p>We may update these Terms and Conditions from time to time. Any updates will be posted on this page with the updated date.</p>
              </div>

              <div className="pt-10 border-t border-brand-olive/10">
                <h2 className="font-serif text-2xl text-brand-olive uppercase tracking-widest mb-4">Contact Us</h2>
                <div className="bg-brand-cream/30 p-6 border-l-4 border-brand-olive">
                  <p className="font-bold">R. Venue Apartelle</p>
                  <p>Lucban, Quezon, Philippines</p>
                  <p>Email: <a href="mailto:rvenueapartelle@lucban.ph" className="underline hover:text-brand-dark">rvenueapartelle@lucban.ph</a></p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TermsConditions;